<script setup lang="ts">
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import { TableCard, TableEmptyState } from '@/components/common/table'
</script>

<template>
  <main class="flex-1 bg-gray-100 min-h-screen dark:bg-gray-800">
    <div class="py-8">
      <div class="flex flex-row max-w-(--breakpoint-2xl) mx-auto items-center mb-6">
        <button @click="goBack" type="button"
          class="flex items-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 mr-4">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
            stroke="currentColor" class="size-5 mr-2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Späť
        </button>
        <PageBreadcrumb page-title="Pridať novú zásielku" />
      </div>
      <div class="max-w-(--breakpoint-2xl) mx-auto">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <!-- Sidebar: Legenda -->
          <aside class="md:col-span-1">
            <div class="bg-white rounded-xl shadow-xs p-6 mb-6 dark:bg-white/3">
              <h3 class="text-lg font-semibold text-gray-800 mb-2 dark:text-white/90">Legenda: Stavy</h3>
              <TableCard title="Stavy">
                <div class="max-w-full overflow-x-auto min-w-0">
                  <Table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800 text-sm">
                    <TableHeader
                      class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
                    >
                      <TableRow>
                        <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                          ID
                        </TableCell>
                        <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                          Názov
                        </TableCell>
                        <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                          Popis
                        </TableCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
                      <TableRow v-for="status in mailStatuses" :key="status.id">
                        <TableCell class="px-6 py-3 text-sm text-gray-700 dark:text-gray-300">{{ status.id }}</TableCell>
                        <TableCell class="px-6 py-3 text-sm text-gray-700 dark:text-gray-300">{{ status.name }}</TableCell>
                        <TableCell class="px-6 py-3 text-sm text-gray-700 dark:text-gray-300">{{ status.description }}</TableCell>
                      </TableRow>
                      <TableEmptyState
                        v-if="mailStatuses.length === 0"
                        title="Žiadne stavy neboli nájdené"
                        hint=""
                        colspan="3"
                      />
                    </TableBody>
                  </Table>
                </div>
              </TableCard>
            </div>
            <div class="bg-white rounded-xl shadow-xs p-6 dark:bg-white/3">
              <h3 class="text-lg font-semibold text-gray-800 mb-2 dark:text-white/90">Legenda: Typy</h3>
              <TableCard title="Typy">
                <div class="max-w-full overflow-x-auto min-w-0">
                  <Table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800 text-sm">
                    <TableHeader
                      class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
                    >
                      <TableRow>
                        <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                          ID
                        </TableCell>
                        <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                          Názov
                        </TableCell>
                        <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                          Popis
                        </TableCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
                      <TableRow v-for="mailType in mailTypes" :key="mailType.id">
                        <TableCell class="px-6 py-3 text-sm text-gray-700 dark:text-gray-300">{{ mailType.id }}</TableCell>
                        <TableCell class="px-6 py-3 text-sm text-gray-700 dark:text-gray-300">{{ mailType.name }}</TableCell>
                        <TableCell class="px-6 py-3 text-sm text-gray-700 dark:text-gray-300">{{ mailType.description }}</TableCell>
                      </TableRow>
                      <TableEmptyState
                        v-if="mailTypes.length === 0"
                        title="Žiadne typy neboli nájdené"
                        hint=""
                        colspan="3"
                      />
                    </TableBody>
                  </Table>
                </div>
              </TableCard>
            </div>
          </aside>

          <!-- Main Form -->
          <section class="md:col-span-2">
            <div v-if="!submitted" class="bg-white rounded-xl shadow-xs p-8 dark:bg-white/3">
              <form @submit.prevent="saveMail" class="space-y-6">
                <div>
                  <label for="sender" class="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Odosielateľ</label>
                  <FormKit type="text"
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm dark:border-gray-700"
                    id="sender" required v-model="mail.sender" name="sender" />
                </div>
                <div>
                  <label for="scan" class="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Scan</label>
                  <FormKit
                    class="block w-full focus:ring-brand-500 focus:border-brand-500 shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
                    id="scan"
                    accept=".pdf,.doc,.docx,.xml,.md,.csv,.jpg,.jpeg,.png,.xls,.xlsx,.txt,.ppt,.pptx,.odt,.ods,.odp,.pages,.rtf,.zip,.rar,.eml,.msg,.html,.htm,.heic"
                    v-on:change="updateImage($event)" name="scan" type="file" />
                </div>
                <div>
                  <label for="envelope_photo" class="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Fotka obálky (voliteľné)</label>
                  <FormKit
                    id="envelope_photo"
                    name="envelope_photo"
                    type="file"
                    accept="image/*,.heic,.heif"
                    :classes="{ input: 'block w-full focus:ring-brand-500 focus:border-brand-500 shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700' }"
                    @change="handleEnvelopePhoto($event)" />
                  <div v-if="envelopePhotoPreviewUrl" class="mt-3 flex items-center gap-3">
                    <img
                      :src="envelopePhotoPreviewUrl"
                      alt="Náhľad obálky"
                      class="size-32 object-cover rounded-md border border-gray-300 cursor-pointer dark:border-gray-700"
                      @click="showEnvelopeLightbox = true" />
                    <button
                      type="button"
                      class="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                      @click="removeEnvelopePhoto">
                      Odstrániť
                    </button>
                  </div>
                </div>
                <div>
                  <label for="user_id" class="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Spoločnosť</label>
                  <FormKit type="autocomplete" id="user_id" name="user_id" v-model="mail.company_id"
                    :options="loadCompanies"
                    placeholder="Vyhľadajte spoločnosť podľa názvu, IČO alebo ID"
                    open-on-click
                    empty-message="Spoločnosť nebola nájdená."
                    class="block w-full">
                    <template #option="{ option }">
                      <div class="formkit-option grow p-2">
                        <div class="font-bold">{{ option.label }}</div>
                        <div class="text-xs text-gray-500 flex flex-wrap gap-x-2 dark:text-gray-400">
                          <span v-if="option.ico">IČO: {{ option.ico }}</span>
                          <span v-if="option.companyId">#{{ option.companyId }}</span>
                          <span v-if="option.vhq_name">VHQ: {{ option.vhq_name }}</span>
                          <span v-if="option.package">Balík: {{ option.package }}</span>
                        </div>
                      </div>
                    </template>
                  </FormKit>
                </div>
                <div>
                  <label for="distribution_date" class="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Dátum
                    doručenia</label>
                  <FormKit
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm dark:border-gray-700"
                    id="distribution_date" type="date" required v-model="mail.distribution_date"
                    name="distribution_date" />
                </div>
                <div>
                  <label for="weight" class="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Gramáž</label>
                  <FormKit type="text" number v-model="mail.weight" name="weight" id="weight"
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm dark:border-gray-700" />
                </div>
                <div class="pt-4">
                  <button type="submit"
                    class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500">
                    Odoslať
                  </button>
                </div>
              </form>
            </div>
            <div v-else class="flex flex-col items-center justify-center bg-white rounded-xl shadow-xs p-8 dark:bg-white/3">
              <h4 class="text-lg font-semibold text-brand-700 mb-4">Zásielka úspešne pridaná!</h4>
              <div class="flex flex-col gap-4 w-full max-w-xs">
                <button
                  class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
                  @click="newMail">
                  Pridať ďalšiu zásielku
                </button>
                <button
                  class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-gray-500 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 dark:bg-gray-700 dark:hover:bg-white/5"
                  @click="backToList">
                  Späť na zoznam zásielok
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
    <vue-easy-lightbox
      v-if="envelopePhotoPreviewUrl"
      :visible="showEnvelopeLightbox"
      :imgs="[envelopePhotoPreviewUrl]"
      :index="0"
      @hide="showEnvelopeLightbox = false" />
  </main>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import MailDataService from '@/services/MailDataService'
import type Mail from '@/types/Mail'
import type ResponseData from '@/types/ResponseData'
import type ObjectConfig from '@/types/ObjectConfig'
import CompanyDataService from '@/services/CompanyDataService'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import imageCompression from 'browser-image-compression'
import heic2any from 'heic2any'
import VueEasyLightbox from 'vue-easy-lightbox'
import 'vue-easy-lightbox/dist/external-css/vue-easy-lightbox.css'
import { Table, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table'
export default defineComponent({
  name: 'mail-add',
  components: { VueEasyLightbox, Table, TableHeader, TableBody, TableRow, TableCell },
  data() {
    return {
      mail: {} as any,
      submitted: false,
      mailStatuses: [] as ObjectConfig[],
      mailTypes: [] as ObjectConfig[],
      imageUrl: '',
      uploadImageData: { name: '', image: '' },
      envelopePhotoFile: null as File | null,
      envelopePhotoPreviewUrl: '' as string,
      showEnvelopeLightbox: false,
    }
  },
  methods: {
    getMailStatuses() {
      MailDataService.getMailStatuses()
        .then((response: ResponseData) => {
          this.mailStatuses = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getMailTypes() {
      MailDataService.getMailTypes()
        .then((response: ResponseData) => {
          this.mailTypes = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    async loadCompanies(ctx: { search?: string }) {
      const term = (ctx?.search || '').trim()
      try {
        const response = await CompanyDataService.getAllCompanies(1, 'id', 'desc', term)
        const data = response.data?.data
        if (!data || !Array.isArray(data.data)) return []
        return data.data.map((c: any) => ({
          label: c.name,
          value: c.id,
          companyId: c.id,
          ico: c.ico,
          vhq_name: c.vhq_name,
          package: c.sidlo_typ_balika,
        }))
      } catch (e: any) {
        toast.error('Chyba: ' + e.message)
        return []
      }
    },
    saveMail() {
      const data = {
        type: 1,
        status: 1,
        scan: this.mail.scan,
        company_id: this.mail.company_id,
        sender: this.mail.sender,
        distribution_date: this.mail.distribution_date,
        isSeen: false,
        weight: this.mail.weight
      }
      // Send the envelope photo (if any) in the SAME request as the row
      // creation. The BE MailController writes the file to disk before
      // queuing the notification email, so the photo URL is baked into
      // the email the customer receives. Previously we uploaded the photo
      // in a second request after add() returned — that raced with the
      // queued email and the photo never made it into the inbox. See
      // docs/mail-envelope-photo.md for the full diagnosis.
      MailDataService.add(data, this.envelopePhotoFile)
        .then((response: ResponseData) => {
          this.mail.id = response.data.id
          if (this.uploadImageData.name != '') {
            MailDataService.uploadImage(this.mail.id, this.uploadImageData)
              .then((response: ResponseData) => {
                this.imageUrl = response.data.image_url
                this.$emit('imageUrl', response.data.secure_url)
              })
              .catch((e: Error) => {
                toast.error('Upload scanu zlyhal: ' + e.message)
              })
          }
          // The envelope photo is now part of the add() request above; no
          // separate upload call here.
          this.submitted = true
        })
        .catch((e: Error) => {
          toast.error('Pridanie zlyhalo: ' + e.message)
        })
    },
    newMail() {
      this.submitted = false
      this.mail = {} as Mail
      this.removeEnvelopePhoto()
    },
    backToList() {
      this.submitted = false
      this.mail = {} as Mail
      this.removeEnvelopePhoto()
      this.$router.push({ name: 'Zoznam zásielok' })
    },
    updateImage(evt: any) {
      this.mail.scan = evt.target.files[0].name
      this.uploadImageData.name = evt.target.files[0].name
      this.uploadImageData.image = evt.target.files[0]
    },
    async handleEnvelopePhoto(evt: any) {
      const original: File = evt.target.files && evt.target.files[0]
      if (!original) return

      try {
        // iPhones default to HEIC since iOS 11. Decode to JPEG in the browser
        // so the file stored on the BE is always a broadly-supported format.
        const isHeic = original.type === 'image/heic' || /\.(heic|heif)$/i.test(original.name)
        const decoded: Blob = isHeic
          ? (await heic2any({ blob: original, toType: 'image/jpeg', quality: 0.92 })) as Blob
          : original

        // Wrap the decoded blob as a File with a .jpg name so the BE validator
        // (mimes:jpg,jpeg,png) accepts it.
        const decodedFile: File = decoded instanceof File
          ? decoded
          : new File([decoded], this.replaceExtension(original.name, 'jpg'), { type: 'image/jpeg' })

        // Compress on a web worker to keep the UI responsive and strip EXIF
        // (including GPS) before it leaves the device.
        const compressed: File = await imageCompression(decodedFile, {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 1600,
          useWebWorker: true,
          fileType: 'image/jpeg',
          initialQuality: 0.85,
        })

        this.envelopePhotoFile = compressed
        // Revoke the previous preview URL before overwriting to avoid a leak
        // when the user re-picks a file before the old preview is closed.
        if (this.envelopePhotoPreviewUrl) {
          URL.revokeObjectURL(this.envelopePhotoPreviewUrl)
        }
        this.envelopePhotoPreviewUrl = URL.createObjectURL(compressed)
        this.showEnvelopeLightbox = false
      } catch (e: any) {
        // Non-fatal: fall back to the existing filename-only behavior so the
        // form still submits. The user can re-pick a different file.
        toast.error('Spracovanie fotky obálky zlyhalo: ' + (e?.message || e))
        this.envelopePhotoFile = null
        this.envelopePhotoPreviewUrl = ''
      }
    },
    removeEnvelopePhoto() {
      this.envelopePhotoFile = null
      if (this.envelopePhotoPreviewUrl) {
        URL.revokeObjectURL(this.envelopePhotoPreviewUrl)
        this.envelopePhotoPreviewUrl = ''
      }
      this.showEnvelopeLightbox = false
      // Reset the file input so the same file can be re-picked if needed.
      const input = document.getElementById('envelope_photo') as HTMLInputElement | null
      if (input) input.value = ''
    },
    replaceExtension(name: string, ext: string) {
      return name.replace(/\.[^./\\]+$/, '') + '.' + ext
    },
    goBack() {
      this.$router.go(-1)
    },
  },
  mounted() {
    this.getMailStatuses()
    this.getMailTypes()
  },
  beforeUnmount() {
    if (this.envelopePhotoPreviewUrl) {
      URL.revokeObjectURL(this.envelopePhotoPreviewUrl)
    }
  },
})
</script>
